import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, MessageSquareWarning, Send } from "lucide-react";
import { toast } from "sonner";
import DocumentUpload from "@/components/DocumentUpload";

export default function AdditionalInfo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(undefined);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [app, setApp] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/applications/${id}/additional-info`);
      if (!data.request) { navigate(`/solicitud/${id}/resultado`); return; }
      setRequest(data.request);
      const res = await api.get(`/applications/${id}`);
      setApp(res.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); navigate("/panel"); }
  };
  useEffect(() => { load(); }, [id]);

  const newDocs = (app?.documents || []).filter((d) => request && d.created_at > request.created_at);

  const submit = async () => {
    if (!newDocs.length) { toast.error("Debes adjuntar la documentación solicitada antes de enviar."); return; }
    if (message.trim().length < 3) { toast.error("Escribe tu respuesta."); return; }
    setBusy(true);
    try {
      await api.post(`/applications/${id}/additional-info`, { request_id: request.id, message });
      toast.success("Antecedentes enviados. Tu solicitud volvió a la cola de revisión.");
      navigate(`/solicitud/${id}/resultado`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (request === undefined) return <AppShell><div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto">
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-6 mb-6 flex gap-3">
          <MessageSquareWarning className="w-7 h-7 text-orange-700 shrink-0" />
          <div>
            <h1 className="text-xl font-bold text-orange-900 mb-2">Necesitamos información adicional</h1>
            <p className="text-sm text-orange-900 whitespace-pre-wrap">{request.message}</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-border p-6 mb-6" data-testid="additional-info-docs">
          <label className="text-sm font-semibold uppercase tracking-wide text-slate-600">1. Adjunta la documentación solicitada (obligatorio)</label>
          <div className="mt-3">
            {app && <DocumentUpload appId={id} docType="adicional" scope="additional" documents={newDocs} onChange={async () => { const r = await api.get(`/applications/${id}`); setApp(r.data); }} />}
          </div>
          {!newDocs.length && <p className="text-xs text-amber-700 mt-2" data-testid="additional-info-missing-docs">Aún no has adjuntado documentos nuevos. Sin ellos no puedes enviar tu respuesta.</p>}
        </div>
        <div className="bg-white rounded-2xl border border-border p-6">
          <label className="text-sm font-semibold uppercase tracking-wide text-slate-600">2. Tu respuesta</label>
          <Textarea className="rounded-xl mt-2 min-h-[140px]" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="additional-info-response" />
          <Button onClick={submit} disabled={busy || !newDocs.length} className="btn-lift w-full h-14 text-lg rounded-xl mt-4" data-testid="btn-send-additional-info">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-5 h-5 mr-2" /> Enviar antecedentes</>}
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
